import styles from './Template.module.scss'
import ServicesFV from 'components/services/common/ServicesFV'
import Value from 'components/services/common/Value'
import type { ValueItem } from 'components/services/common/Value/Value'
import Kind from 'components/services/common/Kind'
import Utilization from 'components/services/common/Utilization'
import type { UtilizationItem } from 'components/services/common/Utilization/Utilization'
import StepPrototyping from 'components/services/common/StepPrototyping'
import type { StepItem } from 'components/services/common/StepPrototyping/StepPrototyping'
import Case3 from 'components/services/common/Case3'
import type { CaseItem3 } from 'components/services/common/Case3/Case3'
import RelatedPosts from 'components/services/common/RelatedPosts'
import type { RelatedPostsItem } from 'components/services/common/RelatedPosts/RelatedPosts'
import FooterCTA from 'components/common/FooterCTA'

const paths = [
  { name: 'TOP', url: '/' },
  { name: 'Services', url: '#' },
  { name: 'プロトタイピング・仮説検証', url: '#' },
]

const ServicesFVList = {
  category: 'デジタルプロダクトデザイン',
  title: (
    <>
      <span>プ</span>
      <span>ロ</span>
      <span>ト</span>
      <span>タ</span>
      <span>イ</span>
      <span>ピ</span>
      <span>ン</span>
      <span>グ</span>
      <span>・</span>
      <span>仮</span>
      <span>説</span>
      <span>検</span>
      <span>証</span>
    </>
  ),
  text: (
    <>
      高速に仮説を検証し、ユーザー体験を最適化します。
      <br />
      アイデアを形にし、素早くフィードバックを得ることで最適な製品開発を実現します。
    </>
  ),
  imgSrc: '/img/services/services_fv_prototyping.webp',
  alt: 'プロトタイピング・仮説検証',
}

const valueList: ValueItem = {
  sectionTitle: 'プロトタイピングの価値',
  sectionRead: (
    <>
      i3DESIGNのプロトタイピングサービスでは、「作りながら考える」アプローチを実践しています。
      <br />
      デザインのご提案では、Figmaを使って複数のアイデアを具体化し、それぞれのメリット・デメリットを整理。
      <br />
      作成時の思考プロセスや意図も合わせてご説明することで、お客様に納得いただける最適な解決策を導き出します。
    </>
  ),
  valueTitle: 'プロトタイピングのメリット',
  valueArray: [
    {
      imgSrc: '/img/services/icon_lisk.svg',
      title: 'リスク低減',
      text: '本開発前に問題点を発見し、手戻りのコストを削減',
    },
    {
      imgSrc: '/img/services/icon_innovative.svg',
      title: '意思決定の迅速化',
      text: '具体的な形があることで、関係者間の認識を合わせ、合意形成を促進',
    },
    {
      imgSrc: '/img/services/icon_mobile.svg',
      title: '早期フィードバック',
      text: '実際に触れるプロトタイプによりユーザーから質の高いフィードバックを獲得',
    },
    {
      imgSrc: '/img/services/icon_inhouse.svg',
      title: 'アイデアの可視化',
      text: '抽象的な概念を具体的な形に変換し、議論を促進',
    },
  ],
}

const utilizationList: UtilizationItem = {
  sectionTitle: 'AIを活用した次世代プロトタイピング',
  sectionRead:
    'i3DESIGNでは最新のAIツールを積極的に活用し、プロトタイピングのスピードと品質を両立させています。これにより、従来では実現できなかった短期間での高度なプロトタイプ制作が可能になりました。',
  utilizationArray: [
    {
      imgSrc: '/img/services/prototyping_utilizatiion01.svg',
      title: 'v0',
      text: 'デザインからコードを生成するAIツール。Figmaデザインからフロントエンドコードを自動生成し、高速にプロトタイプを構築します。直感的な操作でデザインとコード間のギャップを埋め、開発プロセスを効率化します。',
    },
    {
      imgSrc: '/img/services/prototyping_utilizatiion02.svg',
      title: 'Cursor',
      text: 'AI搭載コードエディタ。プロトタイプの微調整や機能追加を効率的に行い、よりリアルな体験を作り出します。コード補完や最適化提案により、エンジニアの生産性を大幅に向上させます。',
    },
    {
      imgSrc: '/img/services/prototyping_utilizatiion03.svg',
      title: 'bolt.new',
      text: 'プロンプトだけでフルスタックWebアプリを構築・デプロイできるAIツール。スクリーンショットからUIを忠実に再現し、実装可能なアプリを短時間で作成。ブラウザ上で完全な開発環境を提供します。',
    },
    {
      imgSrc: '/img/services/prototyping_utilizatiion04.svg',
      title: 'Firebase Studio',
      text: 'Googleのクラウドベース開発環境。自然言語や画像を使ってWebアプリのプロトタイプを素早く作成できます。コードを書かずにアイデアを形にし、ワンクリックでデプロイまで完結。多様な言語とフレームワークに対応しています。',
    },
  ],
}

const stepList: StepItem = {
  sectionTitle: 'プロトタイピングプロセス',
  step: [
    {
      no: '01.',
      title: '要件定義',
      text: 'プロジェクトの目標、ユーザーニーズ、検証したい仮説を明確にします。プロトタイプの目的と範囲を特定し、成功基準を設定します。',
    },
    {
      no: '02.',
      title: 'プロトタイプ制作',
      text: 'Figmaによるビジュアルプロトタイプや、AIツールを活用したテクニカルプロトタイプを制作します。目的に応じて適切な詳細度と忠実度のプロトタイプを選択します。',
    },
    {
      no: '03.',
      title: '検証・フィードバック',
      text: '社内レビューやユーザーテストを通じてフィードバックを収集します。定量・定性データを分析し、デザインの有効性を評価します。',
    },
    {
      no: '04.',
      title: '改善・最適化',
      text: '検証結果に基づいてプロトタイプを改善し、必要に応じて再テストを行います。最終的に本開発へ移行するための準備を整えます。',
    },
  ],
}

const caseList3: CaseItem3 = {
  sectionTitle: '導入事例',
  cases3: [
    {
      no: '(01)',
      title: '新プロダクトのデザインプロトタイプを高速に実施​​',
      sector: '業種：HRTech',
      imgSrc: '/img/services/prototyping_case01.webp',
      alt: '新プロダクトのデザインプロトタイプを高速に実施',
      background:
        '経営陣でプロダクトの構想があったものの、実際のプロジェクト進行にあたり、どのような画面で、どのような機能を有しているかプロジェクトメンバー間でプロダクトイメージの明確な合意形成ができていない状況でした。また、メンバーも日々の業務の中で新規プロダクトに対して十分なリソースを割くことができていない状況でした。',
      solution:
        'メンバーでの完成イメージの共有ができるようなデザインプロトタイプを作ることにしました。想定の機能を2週間ほどで網羅し、さらなるアイデアを加えた高精度のプロトタイプを短期間で納品しました。',
      team1: 'ディレクター1名',
      team2: 'UIデザイナー2名',
      period: '1ヶ月',
    },
    {
      no: '(02)',
      title: '請求書・各種帳票の電子閲覧クラウドサービスのデザイン〜ユーザビリティ検証',
      sector: '業種：大手デジタル支援会社​',
      imgSrc: '/img/services/prototyping_case02.webp',
      alt: '請求書・各種帳票の電子閲覧クラウドサービスのデザイン〜ユーザビリティ検証',
      background:
        '10年以上使われているプロダクトで、ユーザーからの明確な不満はないものの、自社内では使いにくさを指摘されていました。UIもレガシー感があり、ユーザー満足度向上のためにもリニューアルを視野に入れていたところ、利用シーンやユーザビリティの観点から使いにくさを言語化し、これを解消する理想的なデザインを具体化するために実施しました。',
      solution:
        'デザイナーによる既存プロダクトのエキスパートレビューから始め、プロジェクトメンバー内での課題認識を揃え共有しました。その後、デザインプロトタイプを作成し、社内メンバーを被験者としたユーザー検証を行った上でブラッシュアップを行なっています。',
      team1: 'ディレクター1名',
      team2: 'UIデザイナー3名',
      period: (
        <>
          デザインプロトタイプの作成：1ヶ月
          <br />
          ユーザビリティ検証：1ヶ月
        </>
      ),
    },
  ],
}

const relatedPostsList: RelatedPostsItem = {
  sectionTitle: 'プロトタイピング・仮説検証の関連記事',
  relatedPosts: [
    {
      title: 'アイデアを最速で形に！3種のプロトタイプで解決できる課題を解説',
      link: 'https://www.i3design.jp/in-pocket/three-types-of-prototyping/',
      thumbnail: 'https://www.i3design.jp/in-pocket/wp-content/uploads/2025/08/prototyping.png',
      day: '2025年8月8日',
      category: 'UI/UXデザイン',
    },
    {
      title: '最小限の機能で最大の価値を生み出す：MVP開発でのプロトタイプ作成のポイント',
      link: 'https://www.i3design.jp/in-pocket/key-points-of-prototypes/',
      thumbnail:
        'https://www.i3design.jp/in-pocket/wp-content/uploads/2024/12/Thumbnail_PrototypingInMVPDevelopment.png',
      day: '2025年8月1日',
      category: 'UI/UXデザイン',
    },
    {
      title: 'Figmaのバリアブルを使ったプロトタイプ：条件分岐の実例2選',
      link: 'https://www.i3design.jp/in-pocket/prototype-using-variables/',
      thumbnail: 'https://www.i3design.jp/in-pocket/wp-content/uploads/2025/06/variables.png',
      day: '2025年6月25日',
      category: 'UI/UXデザイン',
    },
    {
      title: 'Figmaプロトタイプで、「スクロールすると見た目が変わるヘッダー」を再現する【中級〜上級者向け】',
      link: 'https://www.i3design.jp/in-pocket/changing-header-appearance-on-figma-prototype/',
      thumbnail:
        'https://www.i3design.jp/in-pocket/wp-content/uploads/2024/09/ChangingHeaderAppearanceonScrollInFigma.png',
      day: '2024年12月25日',
      category: 'UI/UXデザイン',
    },
  ],
}

const Prototyping: React.FC = () => {
  return (
    <>
      <ServicesFV ServicesFVEach={ServicesFVList} paths={paths} />
      <Value ValueEach={[valueList]} />
      <div className={styles.backGround}>
        <Kind />
      </div>
      <Utilization UtilizationEach={[utilizationList]} />
      <div className={styles.backGround}>
        <StepPrototyping StepEach={[stepList]} />
      </div>
      <Case3 CaseEach3={[caseList3]} />
      <div className={styles.backGroundGray}>
        <RelatedPosts RelatedPostsEach={[relatedPostsList]} />
      </div>
      <FooterCTA />
    </>
  )
}

export default Prototyping
